/**
 * Other Parallax nodes: their aircraft, cameras and signals, and the controls
 * an admin has over the link (revoke a node, route to its subnet, the CHAOS
 * bridge).
 *
 * A remote aircraft is never a `Drone`. It has its own type, its own key
 * (`node:drone`), and `commandable: false` stated on every record, so nothing
 * that builds a command from a `DroneId` can be handed one by mistake.
 */

import { api, type ApiResult } from '@/api/client';

export type Freshness = 'live' | 'stale';

export interface RemoteAircraft {
  /** `node:drone`. The only id a remote aircraft has on this server. */
  key: string;
  nodeId: string;
  droneId: string;
  callsign: string | null;
  vehicleClass: string | null;
  simulated: boolean;
  linkState: string | null;
  linkAgeS: number | null;
  lat: number | null;
  lon: number | null;
  altRelative: number | null;
  altAmsl: number | null;
  heading: number | null;
  groundSpeed: number | null;
  verticalSpeed: number | null;
  rollDeg: number | null;
  pitchDeg: number | null;
  yawDeg: number | null;
  armed: boolean | null;
  mode: string | null;
  batteryPercent: number | null;
  batteryVoltage: number | null;
  gpsFixType: number | null;
  gpsSatellites: number | null;
  receivedAt: number;
  ageS: number;
  freshness: Freshness;
  remote: true;
  commandable: false;
}

export interface RemoteCamera {
  key: string;
  nodeId: string;
  droneId: string;
  label: string;
  /** Never carries a login: the edge strips it before sending. */
  url: string;
  kind: string;
  playable: 'native' | 'needs-gateway' | 'no';
  needsLogin: boolean;
  receivedAt: number;
  freshness: Freshness;
  /** Where a video gateway would serve it. null until one exists. */
  gateway: string | null;
  remote: true;
}

export type NodeState = 'live' | 'stale' | 'never' | 'revoked';

export interface FederationNode {
  nodeId: string;
  state: NodeState;
  revoked: boolean;
  revokedAt: number | null;
  revokedBy: string | null;
  firstSeen: number | null;
  lastSeen: number | null;
  ageS: number | null;
  reports: number;
  subnets: string[];
  netbirdIp: string | null;
  aircraft: number;
  cameras: number;
  lastResult: { dropped?: Record<string, number> };
}

export interface PushStatus {
  attempts: number;
  successes: number;
  consecutiveFailures: number;
  lastAttemptAt: number | null;
  lastSuccessAt: number | null;
  lastStatus: number | null;
  lastError: string;
  nextDelayS: number;
}

export interface FederationStatus {
  nodeId: string;
  edge:
    | { configured: false }
    | {
        configured: true;
        cloudUrl: string;
        intervalS: number;
        subnets: string[];
        netbirdIp: string | null;
        pushIsr: boolean;
        pushSimulated: boolean;
        status: PushStatus;
      };
  staleAfterS: number;
  expireAfterS: number;
  isr: { catalog: number; sightings: number; evicted: number };
}

export type SignalType =
  | 'wifi'
  | 'bluetooth'
  | 'adsb'
  | 'ais'
  | 'rfid'
  | 'cellular'
  | 'lora'
  | 'zigbee'
  | 'gnss'
  | 'radar'
  | 'rf'
  | 'unknown';

export interface IsrSignal {
  /** `node:signal`. */
  key: string;
  nodeId: string;
  signalId: string;
  signalType: SignalType;
  firstSeen: number;
  lastSeen: number;
  lastObservedAt: number;
  sightings: number;
  lat: number | null;
  lon: number | null;
  altM: number | null;
  strengthDb: number | null;
  frequencyMhz: number | null;
  confidence: number | null;
  /** `node:drone` of the aircraft that carried the sensor, if any. */
  detectedBy: string | null;
  /** The edge's detection time was implausible and the cloud's was used. */
  clockSkewed: boolean;
  details: Record<string, string | number>;
  remote: true;
}

export interface IsrSighting {
  observedAt: number;
  receivedAt: number;
  lat: number | null;
  lon: number | null;
  altM: number | null;
  strengthDb: number | null;
  detectedBy: string | null;
}

export interface NetbirdRoute {
  routeId: string;
  networkId: string;
  network: string;
  nodeId: string | null;
  peerId: string;
  enabled: boolean;
  masquerade: boolean;
}

export interface ChaosStatus {
  enabled: boolean;
  running: boolean;
  state: string;
  connected: boolean;
  lastError?: string | null;
  problems: string[];
  config?: {
    host: string;
    port: number;
    useTls: boolean;
    username: string;
    passwordSet: boolean;
  };
  stats?: {
    messagesReceived: number;
    commandsRefused: number;
    commandsExecuted: number;
    connects: number;
    lastActivity: number | null;
    refusedByType: Record<string, number>;
  };
}

// Polled reads are `logged: false` so a refresh every few seconds does not push
// every real command out of the activity timeline.

export function getRemoteAircraft(): Promise<ApiResult<{ aircraft: RemoteAircraft[]; staleAfterS: number }>> {
  return api.get('/federation/aircraft', { logged: false });
}

export function getRemoteCameras(): Promise<ApiResult<{ cameras: RemoteCamera[] }>> {
  return api.get('/federation/cameras', { logged: false });
}

export function getFederationStatus(): Promise<ApiResult<FederationStatus>> {
  return api.get('/federation/status', { logged: false });
}

export function getNodes(): Promise<ApiResult<{ nodes: FederationNode[] }>> {
  return api.get('/federation/nodes', { logged: false });
}

export function revokeNode(nodeId: string): Promise<ApiResult<{ aircraftDropped: number }>> {
  return api.post(`/federation/nodes/${encodeURIComponent(nodeId)}/revoke`, undefined, {
    label: `REVOKE node ${nodeId}`,
  });
}

export function restoreNode(nodeId: string): Promise<ApiResult<unknown>> {
  return api.post(`/federation/nodes/${encodeURIComponent(nodeId)}/restore`, undefined, {
    label: `RESTORE node ${nodeId}`,
  });
}

export interface SignalQuery {
  type?: SignalType;
  node?: string;
  sinceS?: number;
  limit?: number;
}

export function getSignals(query: SignalQuery = {}): Promise<ApiResult<{ signals: IsrSignal[] }>> {
  return api.get('/federation/isr', { logged: false, query: { ...query } });
}

export function getSightings(
  nodeId: string,
  signalId: string,
): Promise<ApiResult<{ sightings: IsrSighting[] }>> {
  return api.get(
    `/federation/isr/${encodeURIComponent(nodeId)}/${encodeURIComponent(signalId)}/sightings`,
    { logged: false },
  );
}

export function getNetbirdStatus(): Promise<
  ApiResult<{ configured: boolean; host?: string; group?: string }>
> {
  return api.get('/netbird/status', { logged: false });
}

export function getNetbirdRoutes(): Promise<ApiResult<{ routes: NetbirdRoute[] }>> {
  return api.get('/netbird/routes', { logged: false });
}

export function addNetbirdRoute(nodeId: string, subnet: string): Promise<ApiResult<NetbirdRoute>> {
  return api.post('/netbird/routes', { nodeId, subnet }, { label: `ROUTE ${subnet} via ${nodeId}` });
}

export function removeNetbirdRoute(routeId: string): Promise<ApiResult<unknown>> {
  return api.delete(`/netbird/routes/${encodeURIComponent(routeId)}`, {
    label: `REMOVE route ${routeId}`,
  });
}

export function getChaosStatus(): Promise<ApiResult<ChaosStatus>> {
  return api.get('/chaos/status', { logged: false });
}

export function reconnectChaos(): Promise<ApiResult<ChaosStatus>> {
  return api.post('/chaos/connect', {}, { label: 'CHAOS reconnect' });
}

export function disconnectChaos(): Promise<ApiResult<ChaosStatus>> {
  return api.post('/chaos/disconnect', undefined, { label: 'CHAOS disconnect' });
}
